import React from 'react';
import styled from 'styled-components';

const StyledComp = styled.div`
  --font-mono: 'SF Mono', 'Fira Code', Menlo, Consolas, monospace;

  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  font-family: var(--font-mono);
  font-size: 13px;

  .header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fafbfc;
    border-bottom: 1px solid #edf0f2;
  }

  .dots {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .dot--red { background: #ff5f57; }
  .dot--yellow { background: #febc2e; }
  .dot--green { background: #28c840; }

  .title {
    color: #6b7280;
    font-size: 12.5px;
  }

  .header-spacer {
    flex: 1;
  }

  .progress {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .progress-track {
    width: 56px;
    height: 4px;
    border-radius: 2px;
    background: #e5e7eb;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    width: 42.3%;
    background: #111827;
    border-radius: 2px;
  }

  .progress-label {
    color: #9ca3af;
    font-size: 11.5px;
    min-width: 42px;
  }

  .code {
    padding: 8px 0;
  }

  .code-line {
    display: flex;
    padding: 1px 16px 1px 0;
    border-left: 2px solid transparent;
  }

  .code-line--add {
    background: #dafbe1;
    border-left-color: #4ac26b;
  }

  .code-line--modified {
    background: #ffeef0;
    border-left-color: #f85149;
  }

  .line-number {
    flex-shrink: 0;
    width: 40px;
    text-align: right;
    padding-right: 16px;
    color: #a0a8b3;
    user-select: none;
  }

  .line-content {
    color: #1f2328;
    white-space: pre;
  }

  .divider {
    border-top: 1px solid #edf0f2;
    margin: 8px 0 12px;
  }

  .prompt-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 16px 12px;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafbfc;
  }

  .prompt-symbol {
    color: #9ca3af;
  }

  .prompt-text {
    color: #4b5563;
  }

  .thinking-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    margin-bottom: 8px;
    color: #8250df;
    font-style: italic;
    font-size: 12.5px;
  }

  .thinking-dots span {
    display: inline-block;
    animation: thinking-pulse 1.2s infinite ease-in-out;
  }
  .thinking-dots span:nth-child(2) { animation-delay: 0.15s; }
  .thinking-dots span:nth-child(3) { animation-delay: 0.3s; }

  @keyframes thinking-pulse {
    0%, 80%, 100% { opacity: 0.25; }
    40% { opacity: 1; }
  }

  .tool-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 16px;
    font-size: 12.5px;
    color: #4b5563;
  }

  .tool-icon {
    color: #9ca3af;
  }

  .tool-name {
    font-weight: 600;
    color: #111827;
  }

  .tool-arg {
    color: #1a7f37;
  }

  .tool-path {
    color: #6b7280;
  }

  .tool-meta {
    margin-left: auto;
    color: #9ca3af;
    font-style: italic;
  }

  .tool-rows {
    padding-bottom: 12px;
  }
`;

const Comp = ({}) => {
  return (
    <StyledComp>
      <div className="header">
        <div className="dots">
          <span className="dot dot--red" />
          <span className="dot dot--yellow" />
          <span className="dot dot--green" />
        </div>
        <span className="title">quant-research/main</span>
        <div className="header-spacer" />
        <div className="progress">
          <div className="progress-track">
            <div className="progress-fill" />
          </div>
          <span className="progress-label">42.30%</span>
        </div>
      </div>

      <div className="code">
        <div className="code-line">
          <span className="line-number">42</span>
          <span className="line-content">def compute_factor_exposures(ticker: str, benchmark: str = "SPY") -&gt; dict:</span>
        </div>
        <div className="code-line code-line--add">
          <span className="line-number">43</span>
          <span className="line-content">{'    '}prices = client.stocks.prices(ticker, frame="daily")</span>
        </div>
        <div className="code-line">
          <span className="line-number">44</span>
          <span className="line-content">{'    '}bench = client.stocks.prices(benchmark, frame="daily")</span>
        </div>
        <div className="code-line code-line--modified">
          <span className="line-number">47</span>
          <span className="line-content">{'    '}beta = models.beta(prices, bench)</span>
        </div>
        <div className="code-line code-line--add">
          <span className="line-number">48</span>
          <span className="line-content">{'    '}beta_60d = models.beta(prices[-60:], bench[-60:])</span>
        </div>
      </div>

      <div className="divider" />

      <div className="prompt-row">
        <span className="prompt-symbol">{'>'}</span>
        <span className="prompt-text">Add a rolling 60-day beta alongside the full-period calculation.</span>
      </div>

      <div className="thinking-row">
        <span>Thinking...</span>
        <span className="thinking-dots">
          <span>.</span><span>.</span><span>.</span>
        </span>
      </div>

      <div className="tool-rows">
        <div className="tool-row">
          <span className="tool-icon">{'\u203A'}</span>
          <span className="tool-name">grep</span>
          <span className="tool-arg">"rolling"</span>
          <span className="tool-path">src/factors/</span>
          <span className="tool-meta">no matches</span>
        </div>
        <div className="tool-row">
          <span className="tool-icon">{'\u203A'}</span>
          <span className="tool-name">read_file</span>
          <span className="tool-path">src/factors/exposures.py</span>
          <span className="tool-meta">51 lines</span>
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
